import {Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {ActivationEnd, ActivationStart, Event, NavigationCancel, Router} from '@angular/router';
import {WINDOW} from '@app/services/window.service';
import {merge, Subject} from 'rxjs';
import {filter, takeUntil, tap} from 'rxjs/operators';
import {AppLoaderType} from '@app/models/app-loader-type';
import {AppLoaderService} from '@app/services/app-loader.service';
import {PageErrorService} from '@app/pages/page-error/service/page-error.service';
import {SiteSettingsService} from '@app/services/site-settings.service';
import {SiteDomainCode} from '@app/site-settings/site-domain-code';
import {CommonModule} from '@angular/common';
import {AppLoaderLocalizeService} from '@app/components/app-loader/app-loader.localize.service';

@Component({
    selector: 'app-loader',
    templateUrl: './app-loader.component.html',
    imports: [CommonModule],
    styleUrls: ['./app-loader.component.scss'],
    providers: [AppLoaderLocalizeService]
})
export class AppLoaderComponent implements OnInit, OnDestroy {
	public AppLoaderType = AppLoaderType;
	public appLoaderType: AppLoaderType = null;
	public appLoaderTitle: string;
	public isUs: boolean;
	@ViewChild('loader', {static: true}) loaderEl: ElementRef;
	private timer: number = null;

	private destroy$: Subject<void> = new Subject<void>();

	constructor(
		private renderer: Renderer2,
		private router: Router,
		private appLoaderService: AppLoaderService,
		private pageErrorService: PageErrorService,
		@Inject(WINDOW) private window: Window,
		private siteSettingsService: SiteSettingsService,
		private localizeService: AppLoaderLocalizeService
	) {
	}

	public ngOnInit(): void {
		this.isUs = this.siteSettingsService.siteDomainCode === SiteDomainCode.us;
		// PageErrorService
		const pageErrorNotification$ = this.pageErrorService.notification$
			.pipe(
				tap(() => {
					this.hide();
				})
			);

		// Show loader programmatically
		const show$ = this.appLoaderService.show$
			.pipe(
				tap(appLoaderType => {
					this.show(appLoaderType);
				})
			);

		// ActivationStart
		const activationStart$ = this.router.events.pipe(
			filter((event: Event) => event instanceof ActivationStart),
			tap((event: ActivationStart) => {
				if (this.appLoaderType === null) {
					this.appLoaderTitle = event.snapshot.data?.appLoaderTitle ?? '';
					this.show(AppLoaderType.default);
				}
			})
		);

		// ActivationEnd
		const activationEnd$ = this.router.events.pipe(
			filter(event => event instanceof ActivationEnd),
			tap(() => {
				this.hide();
			})
		);

		// NavigationCancel
		const navigationCancel$ = this.router.events.pipe(
			filter(event => event instanceof NavigationCancel),
			tap(() => this.hide())
		);

		merge(activationStart$, activationEnd$, navigationCancel$, show$, pageErrorNotification$)
			.pipe(takeUntil(this.destroy$))
			.subscribe();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
	}

	public getLoaderTitle(): string {
		return this.localizeService.getLoaderTitle(this.appLoaderTitle)
	}

	private show(appLoaderType): void {
		clearTimeout(this.timer);
		this.timer = this.window.setTimeout(() => {
			this.appLoaderType = appLoaderType;
			this.renderer.setStyle(this.loaderEl.nativeElement, 'display', 'block');
			this.renderer.setStyle(this.loaderEl.nativeElement, 'opacity', '1');
		}, 600);
	}

	private hide(): void {
		clearTimeout(this.timer);
		this.renderer.setStyle(this.loaderEl.nativeElement, 'opacity', '0');
		this.timer = this.window.setTimeout(() => {
			this.renderer.setStyle(this.loaderEl.nativeElement, 'display', 'none');
			this.appLoaderType = null;
		}, 350);
	}
}
